import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';
import Navbar from '@/components/Navbar';
import { Minus, Plus, Star, ChevronLeft, ChevronRight, Tag } from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_API_URL;

// Keep a tiny local fallback so detail works without backend
const sampleProducts = [
  {
    id: 'p1',
    name: 'Embroidered Anarkali Gown',
    price: 7499,
    rating: 4.6,
    total_reviews: 128,
    categories: ['bridal', 'women'],
    description: 'A premium embroidered anarkali gown crafted with finesse for special occasions.',
    images: [
      'https://images.unsplash.com/photo-1542060748-10c28b62716b?q=80&w=1200&auto=format&fit=crop',
      'https://images.unsplash.com/photo-1542204165-65bf26472b9b?q=80&w=1200&auto=format&fit=crop',
    ],
  },
  {
    id: 'p2',
    name: 'Classic Men’s Bandhgala',
    price: 5699,
    rating: 4.4,
    total_reviews: 74,
    categories: ['men', 'traditional'],
    description: 'A timeless bandhgala with modern tailoring for a sharp festive look.',
    images: [
      'https://images.unsplash.com/photo-1544735716-392fe2489ffa?q=80&w=1200&auto=format&fit=crop',
      'https://images.unsplash.com/photo-1519741497674-611481863552?q=80&w=1200&auto=format&fit=crop',
    ],
  },
];

const ProductDetail = ({ user }) => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [product, setProduct] = useState(null);
  const [loading, setLoading] = useState(true);
  const [slideIndex, setSlideIndex] = useState(0);
  const [quantity, setQuantity] = useState(1);

  useEffect(() => {
    fetchProduct();
  }, [id]);

  useEffect(() => {
    setSlideIndex(0);
    setQuantity(1);
  }, [id]);

  const fetchProduct = async () => {
    try {
      const response = await axios.get(`${API_URL}/products/${id}`);
      setProduct(response.data);
    } catch (error) {
      // Fallback to sample for missing backend
      const fallback = sampleProducts.find((p) => p.id === id);
      if (fallback) {
        setProduct(fallback);
      } else {
        console.error('Product not found:', error);
        toast.error('Product not found');
        navigate('/products');
        return;
      }
    } finally {
      setLoading(false);
    }
  };

  // Auto-advance slider every 4s when multiple images exist
  const images = (product?.images) || [];
  useEffect(() => {
    if (!images || images.length <= 1) return;
    const id = setInterval(() => {
      setSlideIndex((i) => (i + 1) % images.length);
    }, 4000);
    return () => clearInterval(id);
  }, [images.length]);

  if (loading) {
    return (
      <div className="min-h-screen bg-[#0a0a0a] flex items-center justify-center">
        <div className="spinner"></div>
      </div>
    );
  }

  if (!product) return null;

  const handleClick = () => {
    if (!user) {
      toast.error('Please login to save products to your cart');
      navigate('/auth');
      return;
    }

    const cartStorageKey = `wearoclub_cart_${user.id}`;
    const existingCart = JSON.parse(localStorage.getItem(cartStorageKey) || '[]');
    const existingItemIndex = existingCart.findIndex((item) => item.id === product.id);

    if (existingItemIndex >= 0) {
      const nextCart = [...existingCart];
      nextCart[existingItemIndex] = {
        ...nextCart[existingItemIndex],
        quantity: Number(nextCart[existingItemIndex].quantity || 1) + quantity,
      };
      localStorage.setItem(cartStorageKey, JSON.stringify(nextCart));
      toast.success('Product quantity updated in cart');
      navigate('/dashboard?tab=cart');
      return;
    }

    const nextCart = [
      ...existingCart,
      {
        id: product.id,
        name: product.name,
        price: product.price,
        quantity,
        description: product.description,
        image: images[0] || '',
      }
    ];

    localStorage.setItem(cartStorageKey, JSON.stringify(nextCart));
    toast.success('Product added to cart');
    navigate('/dashboard?tab=cart');
  };

  return (
    <div className="min-h-screen bg-[#0a0a0a]">
      <Navbar user={user} />

      <div className="max-w-7xl mx-auto px-4 py-24">
        <button
          onClick={() => navigate('/products')}
          className="flex items-center gap-2 text-gray-400 hover:text-[#d4a574] mb-8 transition-colors"
        >
          <ChevronLeft className="w-5 h-5" />
          Back to Products
        </button>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
          {/* Left: Image slider */}
          <div className="glass-card overflow-hidden mb-6 relative">
            <img
              src={images[slideIndex] || 'https://via.placeholder.com/600x800?text=Product'}
              alt={product.name}
              className="w-full aspect-[3/4] object-cover select-none"
            />
            {images.length > 1 && (
              <>
                <button
                  type="button"
                  aria-label="Previous image"
                  onClick={() => setSlideIndex((i) => (i - 1 + images.length) % images.length)}
                  className="absolute left-2 top-1/2 -translate-y-1/2 p-2 rounded-full bg-black/40 text-white hover:bg-black/60"
                >
                  <ChevronLeft className="w-5 h-5" />
                </button>
                <button
                  type="button"
                  aria-label="Next image"
                  onClick={() => setSlideIndex((i) => (i + 1) % images.length)}
                  className="absolute right-2 top-1/2 -translate-y-1/2 p-2 rounded-full bg-black/40 text-white hover:bg-black/60"
                >
                  <ChevronRight className="w-5 h-5" />
                </button>
                <div className="absolute bottom-3 left-0 right-0 flex items-center justify-center gap-2">
                  {images.map((_, idx) => (
                    <button
                      key={idx}
                      aria-label={`Go to image ${idx + 1}`}
                      onClick={() => setSlideIndex(idx)}
                      className={`h-2.5 w-2.5 rounded-full ${idx === slideIndex ? 'bg-white' : 'bg-white/40'}`}
                    />
                  ))}
                </div>
              </>
            )}
          </div>

          {/* Right: Details */}
          <div>
            <h1 className="text-4xl sm:text-5xl font-bold mb-4" data-testid="product-name">
              {product.name}
            </h1>

            <div className="flex items-center gap-4 mb-6">
              <div className="flex items-center gap-2 text-[#d4a574]">
                <Star className="w-5 h-5 fill-current" />
                <span className="text-xl font-semibold">{Number(product.rating || 0).toFixed(1)}</span>
                <span className="text-gray-400">({product.total_reviews || 0} reviews)</span>
              </div>
            </div>

            {(product.categories && product.categories.length > 0) && (
              <div className="glass-card p-6 mb-6">
                <div className="flex items-center gap-3 flex-wrap">
                  {product.categories.map((cat, idx) => (
                    <span key={idx} className="px-3 py-1 bg-[#d4a574]/10 text-[#d4a574] rounded-full text-sm flex items-center gap-1">
                      <Tag className="w-3 h-3" /> {cat}
                    </span>
                  ))}
                </div>
              </div>
            )}

            {(product.colors && product.colors.length > 0) && (
              <div className="glass-card p-6 mb-6">
                <h3 className="text-lg font-semibold mb-3">Colours</h3>
                <div className="flex items-center gap-3 flex-wrap">
                  {product.colors.map((c, idx) => (
                    <span key={idx} className="px-3 py-1 bg-[#d4a574]/10 text-[#d4a574] rounded-full text-sm">{c}</span>
                  ))}
                </div>
              </div>
            )}

            {(product.sizes && product.sizes.length > 0) && (
              <div className="glass-card p-6 mb-6">
                <h3 className="text-lg font-semibold mb-3">Sizes</h3>
                <div className="flex items-center gap-3 flex-wrap">
                  {product.sizes.map((s, idx) => (
                    <span key={idx} className="px-3 py-1 bg-[#d4a574]/10 text-[#d4a574] rounded-full text-sm">{s}</span>
                  ))}
                </div>
              </div>
            )}

            <div className="glass-card p-6 mb-6">
              <h3 className="text-xl font-semibold mb-3">About</h3>
              <p className="text-gray-300 leading-relaxed whitespace-pre-line">{product.description || 'Premium handcrafted design from Wearoclub.'}</p>
            </div>

            <div className="glass-card p-6 mb-6">
              <div className="flex items-center justify-between">
                <div className="space-y-4">
                  <div>
                  <p className="text-gray-400 mb-1">Price</p>
                  <p className="text-4xl font-bold text-[#d4a574]">{Number(product.price || 0).toLocaleString('en-IN', { style: 'currency', currency: 'INR' })}</p>
                  </div>
                  <div>
                    <p className="text-gray-400 mb-2">Quantity</p>
                    <div className="inline-flex items-center rounded-xl border border-[#d4a574]/20 bg-[#0f0f0f]">
                      <button
                        type="button"
                        onClick={() => setQuantity((current) => Math.max(1, current - 1))}
                        className="px-3 py-2 text-gray-300 hover:text-[#d4a574] disabled:opacity-40"
                        disabled={quantity <= 1}
                        aria-label="Decrease quantity"
                      >
                        <Minus className="w-4 h-4" />
                      </button>
                      <span className="min-w-12 px-4 py-2 text-center font-semibold text-white">{quantity}</span>
                      <button
                        type="button"
                        onClick={() => setQuantity((current) => current + 1)}
                        className="px-3 py-2 text-gray-300 hover:text-[#d4a574]"
                        aria-label="Increase quantity"
                      >
                        <Plus className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                </div>
                <button className="btn-primary" onClick={handleClick}>Add to Cart</button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProductDetail;
